 
import type {
    BraintreePayPalButtonsComponentProps,
    CreateOrderBraintreeActions,
    OnApproveBraintreeActions,
    OnApproveBraintreeData,
} from '@paypal/react-paypal-js';
import {
    BraintreePayPalButtons,
    FUNDING,
    PayPalScriptProvider,
} from '@paypal/react-paypal-js';
import type {
    OnShippingAddressChangeActions,
    OnShippingAddressChangeData,
    OnShippingOptionsChangeActions,
    OnShippingOptionsChangeData,
} from '@paypal/paypal-js';
import { useContext, useEffect, useRef, useState } from 'react';
import { useExperiment, VariantId } from '@magpie/ui-experiments'; // MS53 : paypal-seamless-checkout : remove when experiment is complete
import paypayCheckout from 'braintree-web/paypal-checkout';
import client from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import { Skeleton } from '@magpie/ui-core';
import styles from './PaypalExpressButton.module.scss';
import type { ClientSettings } from '../../typings/configuration.types';
import { useApi, useExpressWidgetError, useWidgetSettings } from '../../hooks';
import { getForterToken, getErrorMessageDetails, handleComponentError } from '../../utils';
import type {
    ActiveBasketSummaryData,
    BasketCompleteRequest,
    CompleteOrderData,
    ShippingMethod,
    ValidateForterRequest} from '../../typings/basket.types';
import {
    PaymentMethodType
} from '../../typings/basket.types';
import {
    buildCreatePaymentOptions,
    getPreselectedShippingMethodId,
    retrievePayPalDeliveryDetails,
} from '../../utils/paypal';
import priceFormatter from '../../utils/priceFormatter';
import type { GetShippingMethodsQueryParams } from '../../typings/api';
import CheckoutRedirect from '../../utils/checkout-redirect';
import { PAYMENT_TYPES } from '../../typings/payment.types';
import { WidgetContext } from '../../context/WidgetContextProvider/WidgetContextProvider';
import { getFullBasketPrice } from '../../utils/paypal/getFullBasketPrice';
import { AuthorizationError, InvalidAddressDetailsError, StockAllocationError } from '../../errors';
import { FORTER_AUTHORIZATION_STEP } from '../../typings/forterAuthorizationStep';
import { pushPaymentProviderSelectionEvent } from '../../utils/payment-provider-selection-event';
import { HandleGetActiveBasketSummary } from '../../utils/handleGetActiveBasketSummary';
import validateAddress from '../../utils/validateAddress';
import createLineItems from '../../utils/paypal/createLineItems';
import type { PayPalCheckoutInstance } from '../../typings/payPal.types';
import {
    ELLIPSIS,
    NO_ACTIVE_BASKET_SUMMARY_ERROR,
    NO_PAYPAL_ORDER_ID_ERROR,
    NO_PAYPAL_PAYMENT_NONCE_ERROR,
    SHIPPING_OPTION_NAME_MAX_LENGTH,
} from '../../constants/payPalExpress';
import { useOutOfStockRedirectionTimer } from '../../hooks/useOutOfStockRedirectionTimer';
import { shouldEnforceAllocationExpiry } from '../../utils/shouldEnforceAllocationExpiry';

export interface PaypalExpressButtonProperties {
    clientSettings: ClientSettings;
}

interface PayPalButtonContentProps {
    onCreateOrder: BraintreePayPalButtonsComponentProps['createOrder'];
    onShippingAddressChange: (
        data: OnShippingAddressChangeData,
        actions: OnShippingAddressChangeActions,
    ) => Promise<void>;
    onShippingOptionsChange: (
        data: OnShippingOptionsChangeData,
        actions: OnShippingOptionsChangeActions,
    ) => Promise<void>;
    onApprove: (data: OnApproveBraintreeData, actions: OnApproveBraintreeActions) => Promise<void>;
    onError: (err: Record<string, unknown>) => void;
    onCancel: () => void;
}

const PayPalButtonContent = ({
    onCreateOrder,
    onShippingAddressChange,
    onShippingOptionsChange,
    onApprove,
    onError,
    onCancel,
}: PayPalButtonContentProps) => {
    const [state, dispatch] = useContext(WidgetContext);
    const { isPaypalButtonDisplayed } = state;

    const onInit = () =>
        dispatch({
            type: 'SET_IS_PAYPAL_BUTTON_DISPLAYED',
            payload: true,
        });

    return (
        <BraintreePayPalButtons
            data-testid="paypal-buttons"
            style={{
                label: 'buynow',
                height: 55,
            }}
            className={isPaypalButtonDisplayed ? styles.paypalButton : `${styles.paypalButton} ${styles.displayNone}`}
            fundingSource={FUNDING.PAYPAL}
            createOrder={onCreateOrder}
            onShippingAddressChange={onShippingAddressChange}
            onShippingOptionsChange={onShippingOptionsChange}
            onApprove={onApprove}
            onError={onError}
            onInit={onInit}
            onCancel={onCancel}
        />
    );
};

const PaypalExpressButton = ({ clientSettings }: PaypalExpressButtonProperties) => {
    const API = useApi();
    const { setExpressWidgetError } = useExpressWidgetError();
    const { locale, apiBasePath, authToken } = useWidgetSettings();
    const paypalSettings = clientSettings.paymentSettings?.paypalSettings;
    const [paypalCheckoutInstance, setPaypalCheckoutInstance] = useState<PayPalCheckoutInstance | null>(null);
    const [allowedShippingCountry, setAllowedShippingCountry] = useState<boolean>(false);
    const [shippingMethodId, setShippingMethodId] = useState<string>('');
    const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
    const [deviceData, setDeviceData] = useState<string | null>(null);
    const [braintreeToken, setBraintreeToken] = useState<string | null>(null);
    const [paypalMethodId, setPaypalMethodId] = useState<number | null>(null);
    const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
    // MS53 : paypal-seamless-checkout : remove this hook when the experiment is complete
    const [seamlessCheckoutExperimentVariant, isSeamlessExperimentFetchPending, trackSeamlessCheckoutExperiment] =
        useExperiment('paypal-seamless-checkout', '', VariantId.Control);
    // Returns variant value,
    //   or 'Control' when experiment does not exist,
    //   or undefined when experiment is fetching data (isPending: true)
    // trackID withheld from initialization, so it doesn't track until trackSeamlessCheckoutExperiment function is called

    // MS57 : paypal-authorization-step : remove this hook when the experiment is complete
    const [
        preAuthorizationExperimentVariant,
        isPreAuthorizationExperimentFetchPending,
        trackPreAuthorizationExperiment,
    ] = useExperiment('paypal-authorization-step', '', VariantId.Control);

    const redirectOnOutOfStockTimer = useOutOfStockRedirectionTimer();

    const shippingMethodIdRef = useRef(shippingMethodId);
    const shippingMethodsRef = useRef(shippingMethods);
    const deviceDataRef = useRef(deviceData);
    const paypalOrderIdRef = useRef(paypalOrderId);
    const paypalCheckoutInstanceRef = useRef<PayPalCheckoutInstance | null>(paypalCheckoutInstance);
    const allowedShippingCountryRef = useRef(allowedShippingCountry);

    const [state] = useContext(WidgetContext);
    const { isPaypalButtonDisplayed, activeBasketSummary } = state;

    useEffect(() => {
        shippingMethodIdRef.current = shippingMethodId;
    }, [shippingMethodId]);
    useEffect(() => {
        shippingMethodsRef.current = shippingMethods;
    }, [shippingMethods]);
    useEffect(() => {
        deviceDataRef.current = deviceData;
    }, [deviceData]);
    useEffect(() => {
        paypalOrderIdRef.current = paypalOrderId;
    }, [paypalOrderId]);
    useEffect(() => {
        paypalCheckoutInstanceRef.current = paypalCheckoutInstance;
    }, [paypalCheckoutInstance]);
    useEffect(() => {
        allowedShippingCountryRef.current = allowedShippingCountry;
    }, [allowedShippingCountry]);

    useEffect(() => {
        if (!activeBasketSummary) {
            return;
        }

        const initPayPalClient = async () => {
            const tokenData = await API.getBraintreeTokenForGuestBasket();
            setBraintreeToken(tokenData.clientToken);

            try {
                const webClient = await client.create({
                    authorization: tokenData.clientToken,
                });
                
                const payPalCheckoutInstance = (await paypayCheckout.create({
                    client: webClient,
                })) as PayPalCheckoutInstance;

                setPaypalCheckoutInstance(payPalCheckoutInstance);

                const dataCollectorInstance = await dataCollector.create({
                    client: webClient,
                    paypal: true,
                });

                setDeviceData(
                    dataCollectorInstance.deviceData ? dataCollectorInstance.deviceData : 'noDeviceData',
                );
            } catch (error: unknown) {
                handleComponentError(error, setExpressWidgetError);
            }
        };

        const initPaymentMethods = async () => {
            try {
                const data = await API.getActiveBasketPaymentMethods();

                const paypalMethod = data.find((p) => p.type === 'Spree::Gateway::BraintreeVzeroPaypalExpress');
                if (paypalMethod) {
                    setPaypalMethodId(paypalMethod.id);
                } else {
                    throw new Error('could not fetch paypal method id!');
                }
            } catch (error) {
                handleComponentError(error, setExpressWidgetError);
            }
        };

        void initPayPalClient();
        void initPaymentMethods();
    }, [activeBasketSummary]);

    const getNextShippingMethods = async (
        basketId: number,
        query?: GetShippingMethodsQueryParams,
    ): Promise<ShippingMethod[]> => {
        const nextShippingMethods = await API.getShippingMethods(basketId, query);
        const shortenedShippingMethods = nextShippingMethods.map((shippingMethod) => {
            return {
                ...shippingMethod,
                name:
                    shippingMethod.name && shippingMethod.name.length > SHIPPING_OPTION_NAME_MAX_LENGTH
                        ? shippingMethod.name.slice(0, SHIPPING_OPTION_NAME_MAX_LENGTH - ELLIPSIS.length) + ELLIPSIS
                        : shippingMethod.name,
            };
        });

        const categories = new Set(shortenedShippingMethods.map(({ shippingCategoryId }) => shippingCategoryId));
        return categories.size > 1 ? [] : shortenedShippingMethods;
    };

    const updateShippingMethod = async (availableShippingMethods: ShippingMethod[], shippingId: string) => {
        if (!activeBasketSummary) {
            throw new Error(NO_ACTIVE_BASKET_SUMMARY_ERROR);
        }

        const shippingMethod = availableShippingMethods.find(({ id }) => id === +shippingId);

        if (!shippingMethod) {
            throw new Error('No shipping option found');
        }
        await API.updateShippingMethod(
            {
                shippingMethodId: shippingMethod.id,
                shipmentId: shippingMethod.shipmentId,
            },
            activeBasketSummary.id,
        );
    };

    const onShippingOptionsChange = async (
        { selectedShippingOption, orderID }: OnShippingOptionsChangeData,
        actions: OnShippingOptionsChangeActions,
    ) => {
        try {
            if (!allowedShippingCountryRef.current || !orderID) {
                throw new Error("Can't change shipping option");
            }

            if (selectedShippingOption) {
                const nextShippingMethodId = selectedShippingOption.id ?? '';
                return setShippingMethodId(nextShippingMethodId);
            }
             
            return;
        } catch (error) {
            handleComponentError(error, setExpressWidgetError, { preventToast: true });
            return actions.reject();
        }
    };

    const onShippingAddressChange = async (
        data: OnShippingAddressChangeData,
        actions: OnShippingAddressChangeActions,
         
    ) => {
        if (!activeBasketSummary) {
            throw new Error(NO_ACTIVE_BASKET_SUMMARY_ERROR);
        }

        const { shippingAddress } = data;
        const { id: basketId } = activeBasketSummary;

        const query = {
            state: shippingAddress.state,
            city: shippingAddress.city,
            zipCode: shippingAddress.postalCode,
        };
        const { countryCode } = shippingAddress;
        const isAllowedShippingCountry = countryCode === String(locale);
        setAllowedShippingCountry(isAllowedShippingCountry);

        if (!paypalOrderIdRef.current || !isAllowedShippingCountry) {
            return actions.reject();
        }

        try {
            const { current: currentShippingMethodId } = shippingMethodIdRef;

            const nextShippingMethods = await getNextShippingMethods(basketId, query);

            const preselectedShippingMethodId = getPreselectedShippingMethodId(
                nextShippingMethods,
                currentShippingMethodId,
            );

            const fullPrice = getFullBasketPrice(
                activeBasketSummary,
                nextShippingMethods,
                preselectedShippingMethodId,
            );

            const lineItems = createLineItems(activeBasketSummary);

            await paypalCheckoutInstanceRef.current?.updatePayment({
                paymentId: paypalOrderIdRef.current,
                amount: `${priceFormatter(fullPrice)}`,
                currency: activeBasketSummary.currency,
                shippingOptions: nextShippingMethods.map((x, idx) => {
                    const isSelected = preselectedShippingMethodId ? x.id === +preselectedShippingMethodId : idx === 0;
                    const result = {
                        id: x.id,
                        label: x.name ?? '',
                        selected: isSelected,
                        type: 'SHIPPING',
                        amount: {
                            currency: x.currency ?? '',
                            value: x.cost ? `${priceFormatter(x.cost)}` : '0.00',
                        },
                    };

                    return result;
                }),
                lineItems,
            });

            setShippingMethodId(preselectedShippingMethodId);
            setShippingMethods(nextShippingMethods);
        } catch (error) {
            handleComponentError(error, setExpressWidgetError, { preventToast: true });
            return actions.reject();
        }
    };

    const validateBasket = async () => {
        if (!activeBasketSummary) {
            throw new Error(NO_ACTIVE_BASKET_SUMMARY_ERROR);
        }

        try {
            const allocationResult = await API.allocate(activeBasketSummary.id, PAYMENT_TYPES.PAYPAL);

            if (shouldEnforceAllocationExpiry(allocationResult, clientSettings.expressCheckoutOptions)) {
                redirectOnOutOfStockTimer.enableUntil(allocationResult);
            }
        } catch (error) {
            if (error instanceof AuthorizationError) {
                throw error;
            }

            const message = getErrorMessageDetails(error);
            throw new StockAllocationError(message);
        }
    };

    const completeBasket = async (
        basketSummary: ActiveBasketSummaryData,
        nonce: string,
        forterToken: string,
        usePreAuthorization: boolean,
    ): Promise<CompleteOrderData> => {
        const { current: currentDeviceData } = deviceDataRef;

        const completeRequest: BasketCompleteRequest = {
            // build payload to POST
            authorisationStep: usePreAuthorization === true ? FORTER_AUTHORIZATION_STEP.PRE_AUTHORIZATION : '',
            nonce,
            paymentMethod: PaymentMethodType.PayPal,
            storePaymentMethodId: paypalMethodId ?? 0, // This is the payment method Id from the database
            basketId: basketSummary.id,
            deviceData: currentDeviceData, // grab device data from local state
            basketSummary: basketSummary,
            forterToken,
        };

        const completeBasketData = await API.completeBasket(completeRequest);

        return completeBasketData;
    };

    const onCreateOrder = async (_orderData: Record<string, unknown>, _actions: CreateOrderBraintreeActions) => {
        pushPaymentProviderSelectionEvent('PaypalExpress', clientSettings.useMmSessionId);

        setPaypalOrderId(null);
        paypalOrderIdRef.current = null;

        try {
            if (!activeBasketSummary) {
                throw new Error(NO_ACTIVE_BASKET_SUMMARY_ERROR);
            }

            await validateBasket();

            const lineItems = createLineItems(activeBasketSummary);

            const orderId = await paypalCheckoutInstanceRef.current?.createPayment(
                buildCreatePaymentOptions({
                    amount: activeBasketSummary.totalPrice.toString(),
                    currency: clientSettings.currency,
                    lineItems,
                }),
            );

            if (!orderId) {
                throw new Error(NO_PAYPAL_ORDER_ID_ERROR);
            }

            paypalOrderIdRef.current = orderId;
            setPaypalOrderId(orderId);

            return orderId;
        } catch (error) {
            redirectOnOutOfStockTimer.disable();
            handleComponentError(error, setExpressWidgetError);
            throw error;
        }
    };

    const onApprove = async (data: OnApproveBraintreeData, actions: OnApproveBraintreeActions) => {
        try {
            if (!activeBasketSummary) {
                throw new Error(NO_ACTIVE_BASKET_SUMMARY_ERROR);
            }

            if (!actions.order || !paypalOrderIdRef.current) {
                throw new Error(`Something wrong with PapPal provided data ORDER ID:: ${paypalOrderIdRef.current}`);
            }

            const { payer, purchase_units: purchaseUnits } = await actions.order.get();
            if (!purchaseUnits?.length) {
                throw new Error('No purchase units in order');
            }

            const { current: currentShippingMethods } = shippingMethodsRef;
            const { current: currentShippingMethodId } = shippingMethodIdRef;

            let email = payer && 'email_address' in payer ? payer.email_address : undefined;
            const { shipping } = purchaseUnits[0];
            const isMethodUpdated = Boolean(currentShippingMethodId);
            const deliveryDetails = retrievePayPalDeliveryDetails(shipping);
            const validateAddressResult = validateAddress(deliveryDetails);
            if (!validateAddressResult.isValid) {
                const message = validateAddressResult.errors.join(' ');
                throw new InvalidAddressDetailsError(message);
            }

            if (authToken) {
                const user = await API.getUser();
                
                if (user.email) {
                    email = user.email;
                }
            }

            if (email) {
                await API.updateCustomerEmail(email, activeBasketSummary.number, activeBasketSummary.id);
            }

            if (isMethodUpdated) {
                await updateShippingMethod(currentShippingMethods, currentShippingMethodId);
            }

            await API.updateDeliveryDetails(deliveryDetails, activeBasketSummary.id);

            if (
                clientSettings.expressCheckoutOptions?.seamlessPaypalBasketCompletionEnabled &&
                currentShippingMethodId.length > 0
            ) {
                // MS53 : paypal-seamless-checkout : variant = "Control"
                // When experiment is complete:: remove this if condition, the callback and the experiment tracking
                // If variant Control wins:: keep the contents of the eventCallback function only
                if (seamlessCheckoutExperimentVariant === 'Control') {
                    // delay the redirect until tracking is confirmed by passing it as a tracking callback
                    const eventCallback = () =>
                        CheckoutRedirect.RedirectToCheckout(apiBasePath, activeBasketSummary.id, PAYMENT_TYPES.PAYPAL);

                    // tracks if experiment is active,
                    // calls the callback whether active and not
                    trackSeamlessCheckoutExperiment('MS53', eventCallback);
                }
                // MS53 : paypal-seamless-checkout : variant = "A"
                // When experiment is complete:: remove this if condition, and the experiment tracking
                // If variant A wins::  keep the contents of this if condition, and the redirect to order complete
                else if (seamlessCheckoutExperimentVariant === 'A') {
                    // no callback needed - the code that follows has 3 awaits before redirecting
                    trackSeamlessCheckoutExperiment('MS53');
                    const preAuthorizationEnabled =
                        clientSettings.enableForter === true &&
                        clientSettings.enablePayPalPreAuthorization === true;
                    // MS57 : paypal-authorization-step - remove tracking below after experiment finished
                    if (
                        preAuthorizationEnabled &&
                        (preAuthorizationExperimentVariant === 'A' || preAuthorizationExperimentVariant === 'Control')
                    ) {
                        trackPreAuthorizationExperiment('MS57');
                    }

                    const forterToken = getForterToken();
                    const payload = await paypalCheckoutInstanceRef.current?.tokenizePayment(data);
                    const latestBasketSummary = await HandleGetActiveBasketSummary(API, setExpressWidgetError);
                    if (latestBasketSummary) {
                        if (!payload?.nonce) {
                            throw new Error(NO_PAYPAL_PAYMENT_NONCE_ERROR);
                        }

                        // MS57 : paypal-authorization-step - remove code branch below if Control wins
                        if (preAuthorizationEnabled && preAuthorizationExperimentVariant === 'A') {
                            const validateForterRequest: ValidateForterRequest = {
                                basketId: latestBasketSummary.id,
                                forterToken,
                                authorisationStep: FORTER_AUTHORIZATION_STEP.PRE_AUTHORIZATION,
                                paymentMethod: PaymentMethodType.PayPal,
                                payPal: {
                                    email: email ?? '',
                                },
                                billingAddress: deliveryDetails,
                            };

                            const validateForterResult = await API.forterValidate(validateForterRequest);
                            if (validateForterResult.isApproved === true) {
                                const completeData = await completeBasket(
                                    latestBasketSummary,
                                    payload.nonce,
                                    forterToken,
                                    true,
                                );

                                CheckoutRedirect.RedirectToOrderComplete(
                                    apiBasePath,
                                    completeData.orderRef,
                                    PAYMENT_TYPES.PAYPAL,
                                );
                            } else {
                                await API.forterCancel(latestBasketSummary.id);

                                throw new Error('PayPal check failed');
                            }
                        }
                        // MS57 : paypal-authorization-step - remove code branch below if A wins
                        else {
                            try {
                                const completeData = await completeBasket(
                                    latestBasketSummary,
                                    payload.nonce,
                                    forterToken,
                                    false,
                                );

                                CheckoutRedirect.RedirectToOrderComplete(
                                    apiBasePath,
                                    completeData.orderRef,
                                    PAYMENT_TYPES.PAYPAL,
                                );
                            } catch (completeError) {
                                redirectOnOutOfStockTimer.disable();
                                handleComponentError(completeError, setExpressWidgetError);
                            }
                        }
                    }
                }
            } else {
                CheckoutRedirect.RedirectToCheckout(
                    apiBasePath,
                    activeBasketSummary.id,
                    PAYMENT_TYPES.PAYPAL,
                );
            }
        } catch (error) {
            redirectOnOutOfStockTimer.disable();
            handleComponentError(error, setExpressWidgetError);
        }
    };

    // onError is only triggered when there is a technical problem with loading or using the buttons, which is a very rare situation. It is not triggered for card processing problems.
    const onError = (error: Record<string, unknown>) => {
        redirectOnOutOfStockTimer.disable();
        // handle as common error
        handleComponentError(error, setExpressWidgetError, { preventToast: true });
    };

    const onCancel = () => {
        redirectOnOutOfStockTimer.disable();
    };

    return (
        <>
            {(!isPaypalButtonDisplayed || activeBasketSummary === null) && (
                <div data-testid="paypalSkeletonTestId">
                    <Skeleton color="#fde3ad" height="55px" rounded="sm" width="100%" />
                </div>
            )}
            {activeBasketSummary !== null &&
                braintreeToken &&
                paypalMethodId &&
                paypalSettings?.clientId &&
                !isSeamlessExperimentFetchPending &&
                !isPreAuthorizationExperimentFetchPending && (
                    <PayPalScriptProvider
                        options={{
                            clientId: paypalSettings.clientId,
                            currency: clientSettings.currency,
                            merchantId: paypalSettings.merchantId,
                            intent: 'authorize',
                            dataClientToken: braintreeToken,
                        }}
                    >
                        <PayPalButtonContent
                            onError={onError}
                            onApprove={onApprove}
                            onCreateOrder={onCreateOrder}
                            onShippingAddressChange={onShippingAddressChange}
                            onShippingOptionsChange={onShippingOptionsChange}
                            onCancel={onCancel}
                        />
                    </PayPalScriptProvider>
                )}
        </>
    );
};

export default PaypalExpressButton;
